import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

interface DayStep {
  day: number;
  steps: number;
}

interface MonthlyDailyStepsChartProps {
  data?: DayStep[];
  goal?: number;
  daysInMonth?: number;
}

const buildDefault = (n: number): DayStep[] =>
  Array.from({ length: n }, (_, i) => ({
    day: i + 1,
    steps: 8000 + Math.round(Math.sin(i * 0.7) * 4000) + (i % 5) * 700,
  }));

const formatK = (v: number) => {
  if (v >= 1000) {
    const val = v / 1000;
    return `${val.toFixed(val % 1 === 0 ? 0 : 1)}k`;
  }
  return `${v}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block bg-[#20BCBB]" />
        <span className="text-[#1F1F1F] font-medium">Steps · {value.toLocaleString()}</span>
      </div>
    </div>
  );
};

function computeStepsYAxis(
  data: DayStep[],
  goal: number,
): { domain: [number, number]; ticks: number[] } {
  const maxVal = data.reduce((m, d) => Math.max(m, d.steps ?? 0), 0);
  const overall = Math.max(maxVal, goal);
  if (overall <= 0) return { domain: [0, 4000], ticks: [0, 1000, 2000, 3000, 4000] };
  const rawStep = overall / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = Math.ceil(rawStep / magnitude) * magnitude;
  const domainMax = step * 4;
  const ticks = Array.from({ length: 5 }, (_, i) => i * step);
  return { domain: [0, domainMax], ticks };
}

const MonthlyDailyStepsChart: React.FC<MonthlyDailyStepsChartProps> = ({
  data,
  goal = 10000,
  daysInMonth = 30,
}) => {
  const chartData = data ?? buildDefault(daysInMonth);
  const total = chartData.reduce((sum, d) => sum + d.steps, 0);
  const { domain, ticks } = computeStepsYAxis(chartData, goal);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Daily Steps
        </span>
        <div className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter">
          Total : <span className="font-semibold text-[#1F1F1F]">{formatK(total)}</span>
        </div>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fontSize: 10, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              tickFormatter={formatK}
              domain={domain}
              ticks={ticks}
            />
            <ReferenceLine y={goal} stroke="#B7B7B7" strokeDasharray="4 4" strokeWidth={1} />
            <Line
              type="monotone"
              dataKey="steps"
              stroke="#20BCBB"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MonthlyDailyStepsChart;
